import { CONFIG, IS_PROD } from 'src/constants/config';
import { getDashboardRouteName } from 'src/constants/analytics';
import { getDomainNameByEmail } from 'src/constants/utils';
import { UserApiResponse } from 'src/types/user';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (
      command: 'js' | 'config' | 'event' | 'consent',
      target: string | Date,
      params?: Record<string, unknown>,
    ) => void;
  }
}

type AnalyticsPrimitive = string | number | boolean;

export type DashboardAnalyticsParams = Record<
  string,
  AnalyticsPrimitive | null | undefined
>;

interface DashboardAnalyticsContext {
  user?: UserApiResponse | null;
  pathname?: string;
  search?: string;
}

const toAnalyticsValue = (
  value: AnalyticsPrimitive | null | undefined,
): AnalyticsPrimitive | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return value;
};

const normalizeAnalyticsParams = (
  params: DashboardAnalyticsParams,
): Record<string, AnalyticsPrimitive> => {
  return Object.entries(params).reduce<Record<string, AnalyticsPrimitive>>(
    (accumulator, [key, value]) => {
      const normalizedValue = toAnalyticsValue(value);

      if (normalizedValue !== undefined) {
        accumulator[key] = normalizedValue;
      }

      return accumulator;
    },
    {},
  );
};

const canTrackWithGa4 = (): boolean => {
  return Boolean(
    IS_PROD && CONFIG.GA4_MEASUREMENT_ID && typeof window !== 'undefined',
  );
};

const dispatchToGa4 = (
  command: 'event',
  target: string,
  params: Record<string, AnalyticsPrimitive>,
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (typeof window.gtag === 'function') {
    window.gtag(command, target, params);

    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push([command, target, params]);
};

const getSharedContext = ({
  user,
  pathname,
  search,
}: DashboardAnalyticsContext): DashboardAnalyticsParams => {
  return {
    route_name: pathname ? getDashboardRouteName(pathname) : undefined,
    page_path:
      pathname !== undefined
        ? `${pathname}${search ?? ''}` || pathname
        : undefined,
    org_id: user?.organizationId,
    user_id: user?._id,
    user_role: user?.role?.toLowerCase(),
    user_domain: user?.email ? getDomainNameByEmail(user.email) : undefined,
  };
};

export const trackDashboardEvent = (
  eventName: string,
  params: DashboardAnalyticsParams = {},
  context: DashboardAnalyticsContext = {},
): void => {
  if (!canTrackWithGa4()) {
    return;
  }

  const eventParams = normalizeAnalyticsParams({
    ...getSharedContext(context),
    ...params,
  });

  dispatchToGa4('event', eventName, eventParams);
};

export const trackDashboardPageView = (
  context: DashboardAnalyticsContext,
): void => {
  if (!canTrackWithGa4() || typeof window === 'undefined') {
    return;
  }

  const pathname = context.pathname ?? window.location.pathname;
  const search = context.search ?? window.location.search;

  const pageParams = normalizeAnalyticsParams({
    ...getSharedContext({ ...context, pathname, search }),
    page_location: window.location.href,
    page_title: document.title,
  });

  dispatchToGa4('event', 'page_view', pageParams);
};
